import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/lib/auth";
import { useFilters, PRESET_LABELS } from "@/lib/filters";
import { dashApi } from "@/lib/dashApi";
import { fmtRelative } from "@/lib/format";

type NavItem = { label: string; to: string; icon: string; admin?: boolean; end?: boolean };

const NAV: { group: string; items: NavItem[] }[] = [
  {
    group: "Executive",
    items: [
      { label: "Home", to: "/", icon: "🏠", end: true },
      { label: "Daily Briefing", to: "/briefing", icon: "📨" },
      { label: "Classic Overview", to: "/overview", icon: "▣" },
      { label: "Needs Attention", to: "/attention", icon: "⚑" },
      { label: "League Tables", to: "/leagues", icon: "🏆" },
      { label: "Failures", to: "/failures", icon: "▲" },
      { label: "Source Health", to: "/health", icon: "❤" },
    ],
  },
  {
    group: "Decision Centre",
    items: [
      { label: "Decision Centre", to: "/decisions", icon: "🧭", end: true },
      { label: "Today's Priorities", to: "/decisions/priorities", icon: "⚑" },
      { label: "What Changed", to: "/decisions/changes", icon: "Δ" },
      { label: "Risk Register", to: "/decisions/risks", icon: "⚠" },
      { label: "Management Actions", to: "/decisions/actions", icon: "✓" },
      { label: "Accountability", to: "/accountability", icon: "📋", admin: true },
      { label: "Scenario Planning", to: "/scenarios", icon: "🧮", admin: true },
      { label: "Decision Records", to: "/decision-records", icon: "🗳", admin: true },
      { label: "Decision Portfolio", to: "/decision-portfolio", icon: "📊", admin: true },
      { label: "Escalations", to: "/escalations", icon: "🔺" },
    ],
  },
  {
    group: "Business",
    items: [
      { label: "Sales & Finance", to: "/finance", icon: "€" },
      { label: "Stock / Inventory", to: "/stock", icon: "▥" },
      { label: "Subscriptions", to: "/subscriptions", icon: "↻" },
      { label: "Repairs", to: "/repairs", icon: "🔧", end: true },
      { label: "Support", to: "/support", icon: "✉", end: true },
      { label: "Rota / Staff", to: "/rota", icon: "👥" },
      { label: "PIM / Catalogue", to: "/pim", icon: "▦", end: true },
    ],
  },
  {
    group: "Operations",
    items: [
      { label: "Renewals & Billing", to: "/payments/renewals", icon: "💳" },
      { label: "Support Operations", to: "/support/operations", icon: "🎧" },
      { label: "Calls & Voice", to: "/voice/calls", icon: "📞", admin: true },
      { label: "Repair Pipeline", to: "/repairs/pipeline", icon: "🛠" },
      { label: "Catalogue Health", to: "/pim/catalogue-health", icon: "🏷" },
      { label: "Workforce & Capacity", to: "/workforce/capacity", icon: "🗓" },
    ],
  },
  {
    group: "Customers",
    items: [
      { label: "Customers", to: "/customers", icon: "👤", end: true },
      { label: "Rankings", to: "/customers/rankings", icon: "🏅" },
      { label: "Revenue Concentration", to: "/customers/concentration", icon: "◧" },
    ],
  },
  {
    group: "Outlook & Pacing",
    items: [
      { label: "Month-end Outlook", to: "/outlook", icon: "🧮" },
      { label: "Target Pacing", to: "/pacing-targets", icon: "◷" },
      { label: "Forecasting", to: "/forecasting-v2", icon: "📉" },
      { label: "Forecast Accuracy", to: "/forecast-accuracy", icon: "◎" },
      { label: "Board Pack", to: "/board-pack", icon: "📘" },
    ],
  },
  {
    group: "Trust Centre",
    items: [
      { label: "Data Trust", to: "/trust", icon: "🛡", end: true },
      { label: "Connector Freshness", to: "/trust/connectors", icon: "❤" },
      { label: "Reconciliation", to: "/trust/reconciliation", icon: "⚖" },
      { label: "KPI Quality", to: "/trust/kpis", icon: "✓" },
      { label: "Board Pack Trust", to: "/trust/board-pack", icon: "📘" },
      { label: "Review Queue", to: "/trust/reviews", icon: "☑" },
      { label: "Historical Durability", to: "/history/coverage", icon: "🗄", admin: true },
      { label: "Connector Operations", to: "/connectors/operations", icon: "🔌", admin: true },
      { label: "Metric Catalogue", to: "/metrics/catalogue", icon: "🏷", admin: true },
      { label: "Operational Readiness", to: "/operations/readiness", icon: "🩺", admin: true },
    ],
  },
  {
    group: "Reporting",
    items: [
      { label: "Trends", to: "/trends", icon: "📈" },
      { label: "Historical", to: "/history", icon: "🕘" },
      { label: "Company vs", to: "/compare/company", icon: "🏢" },
      { label: "Branch vs", to: "/compare/branch", icon: "📍" },
      { label: "Exec Reports", to: "/reports", icon: "📑" },
      { label: "Digest & Delivery", to: "/digest", icon: "✉" },
      { label: "Notification Centre", to: "/notifications", icon: "🔔" },
    ],
  },
  {
    group: "System",
    items: [
      { label: "Integrations", to: "/integrations", icon: "⇄" },
      { label: "Ecosystem Coverage", to: "/ecosystem", icon: "🗺" },
      { label: "Acceptance Centre", to: "/acceptance", icon: "✅", admin: true },
      { label: "KPI Validation", to: "/validation/kpis", icon: "🔬", admin: true },
      { label: "Admin", to: "/admin", icon: "⚙", admin: true },
    ],
  },
];

function Sidebar() {
  const auth = useAuth();
  const roles = auth.user?.roles ?? [];
  const failures = useQuery({
    queryKey: ["failures-count"],
    queryFn: dashApi.failures,
    refetchInterval: 60_000,
  });
  const critCount = failures.data?.counts?.critical ?? 0;
  const openTotal = failures.data?.total ?? 0;

  return (
    <aside className="flex w-60 flex-shrink-0 flex-col border-r border-ink-500/60 bg-ink-850/80">
      <div className="flex items-center gap-2.5 px-5 py-5">
        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-brand-500 to-brand-700 text-sm font-bold text-white shadow-glow">
          ◆
        </div>
        <div>
          <p className="text-sm font-semibold tracking-tight text-white">IRONDash</p>
          <p className="text-[10px] uppercase tracking-widest text-slate-500">Executive</p>
        </div>
      </div>
      <nav className="flex-1 space-y-5 overflow-y-auto px-3 py-2">
        {NAV.map((section) => {
          const items = section.items.filter((i) => !i.admin || roles.includes("admin"));
          if (!items.length) return null;
          return (
            <div key={section.group}>
              <p className="px-3 pb-1.5 text-[10px] font-semibold uppercase tracking-widest text-slate-600">
                {section.group}
              </p>
              <div className="space-y-0.5">
                {items.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.to === "/" || item.end}
                    className={({ isActive }) =>
                      `group flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors ${
                        isActive
                          ? "bg-brand-500/15 text-white ring-1 ring-brand-500/30"
                          : "text-slate-400 hover:bg-ink-700/70 hover:text-slate-200"
                      }`
                    }
                  >
                    <span className="w-4 text-center text-xs opacity-70">{item.icon}</span>
                    <span className="flex-1">{item.label}</span>
                    {item.to === "/failures" && openTotal > 0 && (
                      <span
                        className={`rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${
                          critCount > 0 ? "bg-rose-500/20 text-rose-300" : "bg-amber-500/20 text-amber-300"
                        }`}
                      >
                        {openTotal}
                      </span>
                    )}
                  </NavLink>
                ))}
              </div>
            </div>
          );
        })}
      </nav>
      <div className="border-t border-ink-500/60 px-3 py-3 text-xs text-slate-500">
        IRONDash · read-only analytics
      </div>
    </aside>
  );
}

function Topbar() {
  const auth = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const f = useFilters();
  const isAdmin = (auth.user?.roles ?? []).includes("admin");
  const [syncMsg, setSyncMsg] = useState<string | null>(null);

  const sync = useMutation({
    mutationFn: dashApi.syncAll,
    onSuccess: async () => {
      setSyncMsg("Synced");
      await qc.invalidateQueries();
      setTimeout(() => setSyncMsg(null), 2500);
    },
    onError: () => setSyncMsg("Sync failed"),
  });

  async function handleLogout() {
    await auth.logout();
    navigate("/login", { replace: true });
  }

  return (
    <header className="sticky top-0 z-10 flex flex-wrap items-center justify-between gap-3 border-b border-ink-500/60 bg-ink-900/80 px-6 py-3 backdrop-blur">
      <div className="flex items-center gap-2">
        <select
          value={f.preset}
          onChange={(e) => f.setPreset(e.target.value as typeof f.preset)}
          className="rounded-lg border border-ink-500/60 bg-ink-800 px-2.5 py-1.5 text-xs font-medium text-slate-200 [color-scheme:dark]"
        >
          {PRESET_LABELS.map((p) => (
            <option key={p.key} value={p.key}>{p.label}</option>
          ))}
          {f.preset === "custom" && <option value="custom">Custom</option>}
        </select>
        <input
          type="date"
          value={f.from}
          max={f.to}
          onChange={(e) => f.setCustom(e.target.value, f.to)}
          className="rounded-md border border-ink-500/60 bg-ink-800 px-2 py-1.5 text-xs text-slate-300 [color-scheme:dark]"
        />
        <span className="text-xs text-slate-600">→</span>
        <input
          type="date"
          value={f.to}
          min={f.from}
          onChange={(e) => f.setCustom(f.from, e.target.value)}
          className="rounded-md border border-ink-500/60 bg-ink-800 px-2 py-1.5 text-xs text-slate-300 [color-scheme:dark]"
        />
        <input
          value={f.branch}
          onChange={(e) => f.setBranch(e.target.value)}
          placeholder="Branch filter…"
          className="w-32 rounded-md border border-ink-500/60 bg-ink-800 px-2 py-1.5 text-xs text-slate-300 placeholder:text-slate-600"
        />
        {f.isLive && (
          <span className="rounded-md bg-sky-500/15 px-2 py-1 text-[10px] font-medium text-sky-300 ring-1 ring-sky-500/30">
            LIVE
          </span>
        )}
      </div>

      <div className="flex items-center gap-3">
        {isAdmin && (
          <button
            type="button"
            onClick={() => sync.mutate()}
            disabled={sync.isPending}
            className="flex items-center gap-1.5 rounded-lg border border-ink-500/60 bg-ink-800 px-3 py-1.5 text-xs font-medium text-slate-200 hover:bg-ink-700 disabled:opacity-60"
          >
            <span className={sync.isPending ? "animate-spin" : ""}>↻</span>
            {syncMsg ?? (sync.isPending ? "Syncing…" : "Sync now")}
          </button>
        )}
        <div className="text-right">
          <p className="text-xs font-medium text-slate-200">{auth.user?.email ?? "—"}</p>
          <p className="text-[10px] text-slate-500">{(auth.user?.roles ?? []).join(", ") || "no roles"}</p>
        </div>
        <button
          type="button"
          onClick={handleLogout}
          className="rounded-lg border border-ink-500/60 bg-ink-800 px-3 py-1.5 text-xs text-slate-300 hover:bg-ink-700"
        >
          Sign out
        </button>
      </div>
    </header>
  );
}

export default function DashLayout() {
  return (
    <div className="flex min-h-screen">
      <Sidebar />
      <div className="flex min-w-0 flex-1 flex-col">
        <Topbar />
        <main className="flex-1 px-6 py-6">
          <div className="mx-auto max-w-[1500px] space-y-6">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}

export function lastUpdatedLabel(captured: string | null | undefined): string {
  return captured ? `Updated ${fmtRelative(captured)}` : "No snapshot yet";
}
